/**
 * Small date helpers working with local calendar days as "YYYY-MM-DD" strings.
 * Dates are created at noon so daylight-saving changes never shift the day.
 */

const pad = (n) => String(n).padStart(2, '0');

export const toIso = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export function parseIso(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12) : null;
}

export const today = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
};

export const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days, 12);

/** Add months, clamping the day (31 Jan + 1 month = 28/29 Feb). */
export function addMonths(date, months) {
    const target = new Date(date.getFullYear(), date.getMonth() + months, 1, 12);
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(date.getDate(), lastDay));
    return target;
}

export const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1, 12);

/** First day of the week for a locale (0 = Sunday … 6 = Saturday). */
export function firstDayOfWeek(locale) {
    try {
        const info = new Intl.Locale(locale);
        const week = info.getWeekInfo?.() ?? info.weekInfo;
        if (week?.firstDay) return week.firstDay % 7;
    } catch {}
    return /^en(-US)?$|^(ja|ko|zh|he|pt-BR)/.test(locale) ? 0 : 1;
}

/** ISO 8601 week number. */
export function isoWeek(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

export const formatDate = (date, locale, options = { dateStyle: 'medium' }) =>
    date ? new Intl.DateTimeFormat(locale, options).format(date) : '';
